import { useEffect, useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button.jsx";
import { Input } from "@/components/ui/input.jsx";
import { cn } from "@/lib/utils.js";
import {
  blurRcaDecimalDraft,
  commitRcaDecimalDraft,
  formatRcaDecimalInput,
  formatRcaDecimalLabel,
  parseRcaDecimalInput,
  roundRcaDecimalInput,
} from "@/components/root-cause-analysis/root-cause-analysis-helpers.js";

export const DEFAULT_FIRST_WINDOW_PCT = 100 / 3;
export const DEFAULT_LAST_WINDOW_HOURS = 2;
export const DEFAULT_SHIFT_THRESHOLD_PCT = 15;

const DRIFT_SETTING_INPUT_CLASS = "h-8 w-[5rem] px-2 py-1 text-xs tabular-nums";

/** Controls min width when input labels wrap/stack (longest label + Apply). */
export const DRIFT_HEADER_CONTROLS_MIN_CLASS = "min-w-[22.25rem]";

/**
 * @param {{
 *   id: string,
 *   label: string,
 *   suffix: string,
 *   draft: string,
 *   onDraftChange: (value: string) => void,
 *   onBlur: () => void,
 *   onApply: () => void,
 *   ariaLabel: string,
 * }} props
 */
function DriftDecimalSettingInput({
  id,
  label,
  suffix,
  draft,
  onDraftChange,
  onBlur,
  onApply,
  ariaLabel,
}) {
  return (
    <label
      htmlFor={id}
      className='flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground'>
      <span>{label}</span>
      <Input
        id={id}
        type='text'
        inputMode='decimal'
        value={draft}
        onChange={(e) => onDraftChange(e.target.value)}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onApply();
          }
        }}
        className={DRIFT_SETTING_INPUT_CLASS}
        aria-label={ariaLabel}
      />
      <span>{suffix}</span>
    </label>
  );
}

/**
 * Draft/applied state for pre-shutdown drift window settings.
 */
export function usePreShutdownDriftSettings() {
  const [firstWindowPct, setFirstWindowPct] = useState(() =>
    roundRcaDecimalInput(DEFAULT_FIRST_WINDOW_PCT)
  );
  const [lastWindowHours, setLastWindowHours] = useState(() =>
    roundRcaDecimalInput(DEFAULT_LAST_WINDOW_HOURS)
  );
  const [shiftThresholdPct, setShiftThresholdPct] = useState(
    DEFAULT_SHIFT_THRESHOLD_PCT
  );
  const [firstWindowPctDraft, setFirstWindowPctDraft] = useState(() =>
    formatRcaDecimalInput(DEFAULT_FIRST_WINDOW_PCT)
  );
  const [lastWindowHoursDraft, setLastWindowHoursDraft] = useState(() =>
    formatRcaDecimalInput(DEFAULT_LAST_WINDOW_HOURS)
  );
  const [shiftThresholdPctDraft, setShiftThresholdPctDraft] = useState(
    DEFAULT_SHIFT_THRESHOLD_PCT
  );

  useEffect(() => {
    setShiftThresholdPctDraft(shiftThresholdPct);
  }, [shiftThresholdPct]);

  function applyDriftSettings() {
    commitRcaDecimalDraft(
      firstWindowPctDraft,
      setFirstWindowPct,
      setFirstWindowPctDraft,
      (n) => n > 0 && n < 100
    );
    commitRcaDecimalDraft(
      lastWindowHoursDraft,
      setLastWindowHours,
      setLastWindowHoursDraft,
      (n) => n > 0
    );
    if (
      Number.isFinite(shiftThresholdPctDraft)
      && shiftThresholdPctDraft >= 0
    ) {
      setShiftThresholdPct(shiftThresholdPctDraft);
    }
  }

  const driftConfig = useMemo(
    () => ({
      firstWindowPct,
      lastWindowHours,
      shiftThresholdPct,
    }),
    [firstWindowPct, lastWindowHours, shiftThresholdPct]
  );

  const firstWindowLabel = `${formatRcaDecimalLabel(firstWindowPct)}%`;
  const lastWindowLabel =
    lastWindowHours === 1
      ? "1 hour"
      : `${formatRcaDecimalLabel(lastWindowHours)} hours`;

  const firstWindowPctDraftNum = parseRcaDecimalInput(firstWindowPctDraft);
  const lastWindowHoursDraftNum = parseRcaDecimalInput(lastWindowHoursDraft);

  const settingsApplyDisabled =
    !Number.isFinite(firstWindowPctDraftNum)
    || firstWindowPctDraftNum <= 0
    || firstWindowPctDraftNum >= 100
    || !Number.isFinite(lastWindowHoursDraftNum)
    || lastWindowHoursDraftNum <= 0
    || !Number.isFinite(shiftThresholdPctDraft)
    || shiftThresholdPctDraft < 0;

  return {
    driftConfig,
    firstWindowLabel,
    lastWindowLabel,
    shiftThresholdPct,
    toolbarProps: {
      applyDriftSettings,
      settingsApplyDisabled,
      firstWindowPctDraft,
      setFirstWindowPctDraft,
      lastWindowHoursDraft,
      setLastWindowHoursDraft,
      shiftThresholdPctDraft,
      setShiftThresholdPctDraft,
    },
  };
}

/**
 * @param {{
 *   className?: string,
 *   onClick?: (e: React.MouseEvent) => void,
 *   applyDriftSettings: () => void,
 *   settingsApplyDisabled: boolean,
 *   firstWindowPctDraft: string,
 *   setFirstWindowPctDraft: (value: string) => void,
 *   lastWindowHoursDraft: string,
 *   setLastWindowHoursDraft: (value: string) => void,
 *   shiftThresholdPctDraft: number,
 *   setShiftThresholdPctDraft: (value: number) => void,
 * }} props
 */
export function PreShutdownDriftSettingsToolbar({
  className,
  onClick,
  applyDriftSettings,
  settingsApplyDisabled,
  firstWindowPctDraft,
  setFirstWindowPctDraft,
  lastWindowHoursDraft,
  setLastWindowHoursDraft,
  shiftThresholdPctDraft,
  setShiftThresholdPctDraft,
}) {
  const firstWindowPctInputId = useId();
  const lastWindowHoursInputId = useId();
  const shiftThresholdPctInputId = useId();

  return (
    <div
      className={cn(
        "flex flex-[0_1_auto] items-start justify-end gap-2",
        DRIFT_HEADER_CONTROLS_MIN_CLASS,
        className
      )}
      onClick={onClick}>
      <div className='flex min-w-0 flex-1 flex-wrap content-start items-center justify-end gap-x-2 gap-y-1.5'>
        <DriftDecimalSettingInput
          id={firstWindowPctInputId}
          label='First window'
          suffix='%'
          draft={firstWindowPctDraft}
          onDraftChange={setFirstWindowPctDraft}
          onBlur={() =>
            blurRcaDecimalDraft(firstWindowPctDraft, setFirstWindowPctDraft)
          }
          onApply={applyDriftSettings}
          ariaLabel='First window percent of trend'
        />
        <DriftDecimalSettingInput
          id={lastWindowHoursInputId}
          label='Last window'
          suffix='h'
          draft={lastWindowHoursDraft}
          onDraftChange={setLastWindowHoursDraft}
          onBlur={() =>
            blurRcaDecimalDraft(lastWindowHoursDraft, setLastWindowHoursDraft)
          }
          onApply={applyDriftSettings}
          ariaLabel='Last window hours'
        />
        <label
          htmlFor={shiftThresholdPctInputId}
          className='flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground'>
          <span>Shift threshold</span>
          <Input
            id={shiftThresholdPctInputId}
            type='number'
            inputMode='decimal'
            min={0}
            step={1}
            value={
              Number.isFinite(shiftThresholdPctDraft)
                ? shiftThresholdPctDraft
                : ""
            }
            onChange={(e) => {
              const n = e.target.valueAsNumber;
              setShiftThresholdPctDraft(
                e.target.value === "" || !Number.isFinite(n) ? NaN : n
              );
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyDriftSettings();
              }
            }}
            className='h-8 w-[3.5rem] px-2 py-1 text-xs tabular-nums'
            aria-label='Minimum shift as percent of observed range'
          />
          <span>% of range</span>
        </label>
      </div>
      <Button
        type='button'
        size='sm'
        variant='outline'
        className='h-8 shrink-0 self-start px-2.5 text-xs'
        disabled={settingsApplyDisabled}
        onClick={applyDriftSettings}>
        Apply
      </Button>
    </div>
  );
}
